import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResponseObj } from '../models/response.model';
import { unwrapResponse } from '../http/unwrap-response';
import {
  ChangePasswordRequest,
  LoginRequest,
  LoginResult,
  Session,
  VerifyOtpRequest,
  VerifyOtpResult,
} from '../models/auth.model';

const SESSION_STORAGE_KEY = 'outward-approval.session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = `${environment.apiBaseUrl}/outward-approval/auth`;
  private readonly session = signal<Session | null>(this.readStoredSession());

  readonly currentSession = this.session.asReadonly();
  readonly isAuthenticated = computed(() => this.session() !== null);
  readonly role = computed(() => this.session()?.role ?? null);

  constructor(private readonly http: HttpClient) {}

  login(request: LoginRequest): Observable<LoginResult> {
    return this.http
      .post<ResponseObj<LoginResult>>(`${this.baseUrl}/login`, request)
      .pipe(unwrapResponse());
  }

  verifyOtp(request: VerifyOtpRequest): Observable<VerifyOtpResult> {
    return this.http
      .post<ResponseObj<VerifyOtpResult>>(`${this.baseUrl}/verify-otp`, request)
      .pipe(
        unwrapResponse(),
        tap((result) => {
          this.setSession({
            sessionToken: result.sessionToken,
            email: result.email,
            role: result.role,
          });
        }),
      );
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http
      .post<ResponseObj<unknown>>(`${this.baseUrl}/change-password`, request)
      .pipe(
        unwrapResponse(),
        map(() => undefined),
        tap(() => this.clearSession()),
      );
  }

  logout(): Observable<void> {
    return this.http.post<ResponseObj<unknown>>(`${this.baseUrl}/logout`, {}).pipe(
      unwrapResponse(),
      map(() => undefined),
      tap({
        next: () => this.clearSession(),
        // even if the network call fails, drop the local session
        error: () => this.clearSession(),
      }),
    );
  }

  clearSession(): void {
    this.session.set(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }

  private setSession(session: Session): void {
    this.session.set(session);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  }

  private readStoredSession(): Session | null {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as Session;
    } catch {
      return null;
    }
  }
}
